import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { MediaTile } from "@/components/MediaTile";
import { MediaLightbox } from "@/components/MediaLightbox";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sparkles, Loader2, Search, Bookmark, ListTree } from "lucide-react";

const EXAMPLES = [
  "milfs wearing black flip flops outdoors in the USA",
  "barefoot at an entrance",
  "red toenails on the beach",
];

export const AISearchDialog = ({ open, onClose }) => {
  const { user, requireAction } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null); // {filters, count, results}
  const [openId, setOpenId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [library, setLibrary] = useState("all");
  const [showDiag, setShowDiag] = useState(false);
  const LIBS = [["all", "All"], ["originals", "Originals"], ["celebrities", "Celebrities"], ["pornstars", "Pornstars"], ["web_finds", "Best of the Web"], ["community", "Community"]];

  const run = async (query, lib) => {
    const text = (query ?? q).trim();
    if (!text || loading) return;
    setQ(text);
    setLoading(true);
    try {
      const useLib = lib ?? library;
      const r = await api.post("/search/ai", { query: text, library: useLib === "all" ? null : useLib });
      setRes(r.data);
    } catch { toast.error("Search failed — try again"); }
    setLoading(false);
  };

  const saveSmart = async () => {
    if (!user) { requireAction?.(); return; }
    if (saving) return;
    setSaving(true);
    try {
      const r = await api.post("/collections/smart", { query: q });
      toast.success("Saved as a smart gallery — it'll auto-update with new matches");
      onClose();
      navigate(`/collection/${r.data.id}`);
    } catch { toast.error("Couldn't save gallery"); }
    setSaving(false);
  };

  const filters = res?.filters;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="ai-search-dialog" className="max-w-4xl bg-card border-border max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-xl"><Sparkles className="w-5 h-5 text-primary" /> AI Search</DialogTitle>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input data-testid="ai-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") run(); }}
            placeholder="Describe what you're looking for…" className="pl-9 pr-24 bg-secondary/50 border-border h-11" />
          <Button data-testid="ai-search-submit" onClick={() => run()} disabled={loading} size="sm" className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-full h-8">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid="ai-search-library-filter">
          <span className="text-xs text-muted-foreground mr-1">Library:</span>
          {LIBS.map(([v, l]) => (
            <button key={v} data-testid={`ai-lib-${v}`} onClick={() => { setLibrary(v); if (res || q.trim()) run(undefined, v); }}
              className={"text-xs rounded-full px-3 py-1 border transition-colors " + (library === v ? "bg-primary text-primary-foreground border-primary font-medium" : "border-border text-muted-foreground hover:text-foreground hover:border-primary/40")}>
              {l}
            </button>
          ))}
        </div>

        {!res && !loading && (
          <div className="flex flex-wrap gap-2 mt-1">
            <span className="text-xs text-muted-foreground py-1">Try:</span>
            {EXAMPLES.map((ex) => (
              <button key={ex} data-testid="ai-example" onClick={() => run(ex)} className="text-xs rounded-full bg-secondary/60 border border-border px-3 py-1 hover:border-primary/50 text-muted-foreground hover:text-foreground transition-colors">{ex}</button>
            ))}
          </div>
        )}

        {res && (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-1.5 flex-wrap text-xs">
              <span className="text-muted-foreground" data-testid="ai-result-count">{res.count === 0 ? "0 strong matches" : res.count > res.results.length ? `Top ${res.results.length} of ${res.count}` : `${res.count} ${res.count === 1 ? "match" : "matches"}`} ·</span>
              {(filters?.required_concepts || []).map((c) => (
                <span key={c.name} data-testid={`ai-concept-${c.name.replace(/\s+/g, "-")}`} title={c.hard ? "Hard requirement — owner tags win" : "Context requirement"}
                  className={"rounded-full px-2 py-0.5 border " + (c.hard ? "bg-primary/15 border-primary/40 text-primary font-medium" : "bg-sky-500/10 border-sky-500/30 text-sky-300")}>
                  {c.kind === "text" ? c.label : c.name}{c.hard ? " · required" : ""}
                </span>
              ))}
              {(filters?.named_subjects || []).map((n) => <span key={n} data-testid="ai-filter-subject" className="rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 px-2 py-0.5">{n}</span>)}
              {filters?.library && <span className="rounded-full bg-secondary/60 border border-border px-2 py-0.5 text-muted-foreground capitalize">{filters.library}</span>}
              {filters?.media_type && filters.media_type !== "any" && <span className="rounded-full bg-secondary/60 border border-border px-2 py-0.5 text-muted-foreground">{filters.media_type}s</span>}
              {filters?.country && <span className="rounded-full bg-secondary/60 border border-border px-2 py-0.5 text-muted-foreground">{filters.country}</span>}
              {filters?.city && <span className="rounded-full bg-secondary/60 border border-border px-2 py-0.5 text-muted-foreground">{filters.city}</span>}
            </div>
            <div className="flex items-center gap-2">
              {user?.is_admin && res.diagnostics && (
                <Button data-testid="ai-diagnostics-toggle" onClick={() => setShowDiag((v) => !v)} size="sm" variant="ghost" className="rounded-full text-xs text-muted-foreground">
                  <ListTree className="w-4 h-4 mr-1" /> Diagnostics ({res.tiers?.candidates ?? res.diagnostics.length})
                </Button>
              )}
              <Button data-testid="ai-save-smart" onClick={saveSmart} disabled={saving || res.count === 0} size="sm" variant="outline" className="rounded-full border-primary/40 text-primary">
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Bookmark className="w-4 h-4 mr-1" />} Save as smart gallery
              </Button>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto min-h-0 mt-1 space-y-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-muted-foreground">
              <Loader2 className="w-7 h-7 animate-spin text-primary" />
              <p className="text-sm">Finding the best matches…</p>
            </div>
          ) : res ? (
            <>
              {showDiag && user?.is_admin && res.diagnostics && <SearchDiagnostics rows={res.diagnostics} tiers={res.tiers} />}
              {res.results.length === 0 ? (
                <div data-testid="ai-no-strong" className="rounded-xl border border-border bg-secondary/30 px-4 py-6 text-center">
                  <p className="text-sm font-medium">No strong matches found for “{res.query || q}.”</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {(filters?.required_concepts || []).some((c) => c.hard)
                      ? "Nothing satisfies every required part of your request. Owner tags decide what counts — a post tagged flip flops or sandals can never count as barefoot."
                      : "Try different words or fewer requirements."}
                  </p>
                </div>
              ) : (
                <div data-testid="ai-results" className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {res.results.map((m) => <TierTile key={m.id} item={m} onOpen={() => setOpenId(m.id)} />)}
                </div>
              )}
              {res.closest?.length > 0 && (
                <section data-testid="ai-closest" className="rounded-xl border border-dashed border-amber-500/40 bg-amber-500/5 p-3">
                  <div className="flex items-baseline justify-between gap-2 flex-wrap mb-2">
                    <h3 className="text-sm font-medium text-amber-200">Closest matches <span className="text-amber-300/70 font-normal">· {res.closest.length}</span></h3>
                    <p className="text-xs text-amber-200/70">Your core requirement still holds{(filters?.required_concepts || []).some((c) => c.hard) ? ` (${(filters.required_concepts || []).filter((c) => c.hard).map((c) => c.name).join(", ")} confirmed)` : ""} — the rest of the request is only weakly supported. These are not true matches.</p>
                  </div>
                  <div data-testid="ai-closest-results" className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {res.closest.map((m) => <TierTile key={m.id} item={m} onOpen={() => setOpenId(m.id)} />)}
                  </div>
                </section>
              )}
            </>
          ) : null}
        </div>

        <MediaLightbox mediaId={openId} open={!!openId} onClose={() => setOpenId(null)} onNavigate={setOpenId} playlist={[...(res?.results || []), ...(res?.closest || [])]} />
      </DialogContent>
    </Dialog>
  );
};

const TIER_STYLE = {
  strong: "bg-emerald-500/90 text-white", good: "bg-sky-500/90 text-white", closest: "bg-amber-500/90 text-black",
};
const TIER_LABEL = { strong: "Strong match", good: "Good match", closest: "Closest match" };

const TierTile = ({ item, onOpen }) => (
  <div className="relative" data-testid={`ai-tile-${item.id}`} data-tier={item.match_tier}>
    <MediaTile item={item} onOpen={onOpen} />
    {item.match_tier && (
      <span data-testid="ai-tier-badge" title={item.match_reason}
        className={"absolute bottom-2 right-2 z-10 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide shadow pointer-events-none " + (TIER_STYLE[item.match_tier] || "bg-secondary")}>
        {TIER_LABEL[item.match_tier] || item.match_tier}
      </span>
    )}
  </div>
);

const SearchDiagnostics = ({ rows, tiers }) => (
  <div data-testid="ai-search-diagnostics" className="rounded-xl border border-border bg-black/30 p-3 text-xs">
    <div className="flex items-center gap-3 flex-wrap mb-2 text-muted-foreground">
      <span className="font-medium text-foreground">Admin search diagnostics</span>
      <span>candidates {tiers?.candidates}</span><span className="text-emerald-400">strong {tiers?.strong}</span>
      <span className="text-sky-400">good {tiers?.good}</span><span className="text-amber-400">closest {tiers?.closest}</span>
      <span className="text-red-400">rejected {tiers?.rejected}</span>
    </div>
    <div className="overflow-x-auto max-h-64 overflow-y-auto">
      <table className="w-full text-left align-top">
        <thead className="text-muted-foreground sticky top-0 bg-[#0b0b0b]">
          <tr><th className="pr-2 py-1">Tier</th><th className="pr-2">Title / ID</th><th className="pr-2">manual_tags</th><th className="pr-2">Hard req</th><th className="pr-2">Contradiction</th><th className="pr-2">Evidence</th><th>Score</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.media_id} data-testid={`ai-diag-row-${r.media_id}`} data-tier={r.tier} className="border-t border-border/50">
              <td className="pr-2 py-1 whitespace-nowrap"><span className={"rounded px-1.5 py-0.5 " + (r.tier === "rejected" ? "bg-red-500/20 text-red-300" : r.tier === "closest" ? "bg-amber-500/20 text-amber-200" : r.tier === "good" ? "bg-sky-500/20 text-sky-200" : "bg-emerald-500/20 text-emerald-200")}>{r.tier}</span></td>
              <td className="pr-2 py-1 max-w-[200px]"><div className="truncate" title={r.title}>{r.title || "(untitled)"}</div><div className="text-muted-foreground font-mono text-[10px]">{r.media_id.slice(0, 8)} · {r.media_type}</div></td>
              <td className="pr-2 py-1 max-w-[180px] text-muted-foreground">{Array.isArray(r.manual_tags) ? (r.manual_tags.join(", ") || "—") : "legacy (none)"}<div className="text-[10px] opacity-70">eff: {(r.effective_tags || []).join(", ") || "—"}</div></td>
              <td className="pr-2 py-1 whitespace-nowrap">{r.hard_ok ? "YES" : "NO"}</td>
              <td className="pr-2 py-1 max-w-[220px]">{r.contradiction ? <span className="text-red-300">YES — {r.contradiction_reason}</span> : "NO"}</td>
              <td className="pr-2 py-1 max-w-[320px]">
                {Object.entries(r.evidence || {}).map(([k, e]) => (
                  <div key={k}><span className="font-medium">{k}</span> <span className="text-muted-foreground">[{e.level}]</span> {e.reason}{e.text ? <span className="opacity-70"> · text: {e.text}</span> : null}{e.visual ? <span className="opacity-70"> · visual: {e.visual}</span> : null}</div>
                ))}
                <div className="text-muted-foreground italic">{r.reason}</div>
              </td>
              <td className="py-1">{r.semantic_score}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);
